<template>
  <div class="ly-evwrap">
  <!-- корень есть всегда: на нём висит ResizeObserver, панель внутри появляется и пропадает.
       Комментарий внутри корня: снаружи он делает корней два, и $el становится текстом. -->
  <div v-if="shown" class="ly-evbar">
    <div class="ly-ev-head">
      <span class="tlabel">{{ inScene ? 'события столбца' : 'события' }}</span>
      <span class="ly-ev-info">{{ all.length }} {{ plural(all.length, ['отметка', 'отметки', 'отметок']) }}
        · {{ dayLabel(minD) }} — {{ dayLabel(maxD) }}<template v-if="inScene"> · {{ hint }}</template></span>
      <span v-for="k in EV_ORDER" :key="k" class="ly-ev-lg" :style="{ color: EV[k].c }"><b>{{ EV[k].i }}</b>{{ EV[k].n }}</span>
    </div>

    <!-- журнал: номера совпадают с метками в сцене -->
    <div v-if="inScene" class="ly-ev-log">
      <button v-for="e in all" :key="e.id" type="button" class="ly-ev-row" :class="{ 'is-on': e.id === selected }"
              :style="{ '--c': EV[e.kind].c }" @click="pick(e.id)">
        <u>{{ e.id + 1 }}</u><i></i><b>{{ e.title }}</b><em>{{ where(e) }}</em><s>{{ dayLabel(e.day) }}</s>
      </button>
    </div>

    <!-- лента: своя дорожка на тип, координата — дата -->
    <template v-if="mode === 'score'">
      <div ref="score" class="ly-ev-score">
        <div v-for="ln in lanes" :key="ln.k" class="ly-ev-lane">
          <span class="ly-ev-lname" :style="{ color: EV[ln.k].c }">{{ EV[ln.k].n }}</span>
          <div class="ly-ev-track">
            <!-- сперва подложка диапазонов, потом точки: иначе полоса перечёркивает подпись -->
            <i v-for="b in ln.bars" :key="'b' + b.id" class="ly-ev-range" :style="{ left: b.x + '%', width: b.w + '%', '--c': EV[ln.k].c }"></i>
            <button v-for="d in ln.dots" :key="d.e.id" type="button" class="ly-ev-dot"
                    :class="{ 'is-on': d.e.id === selected, 'ly-ev-flip': d.flip }" :style="{ left: d.x + '%', '--c': EV[d.e.kind].c }"
                    :title="d.e.title + ' · ' + dayLabel(d.e.day)" @click="pick(d.e.id)">
              <i></i><span v-if="d.lab">{{ d.e.id + 1 }} · {{ d.e.title }}</span>
            </button>
          </div>
        </div>
      </div>
      <div class="ly-ev-axis"><span v-for="a in axis" :key="a.x" :style="{ left: a.x + '%' }">{{ a.t }}</span></div>
    </template>

    <!-- воронка: ступень — первая сессия каждого типа конверсии -->
    <template v-if="mode === 'funnel'">
      <div class="ly-ev-funnel">
        <template v-for="(s, k) in steps" :key="s.e.id">
          <div v-if="k > 0" class="ly-ev-arrow"><i></i>
            <span>{{ s.dd > 0 ? '+' + s.dd + ' ' + plural(s.dd, DAYS) : 'в тот же день' }}<br>+{{ s.gain }} {{ plural(s.gain, CONV) }}</span>
          </div>
          <button type="button" class="ly-ev-step" :class="{ 'is-on': s.e.id === selected }" @click="pick(s.e.id)">
            <b>{{ s.type }}</b><s>{{ dayLabel(s.e.day) }}</s>
            <em>сессия №{{ s.e.i + 1 }} · конверсия {{ s.acc }} из {{ s.tot }}</em>
          </button>
        </template>
        <span v-if="!steps.length" class="ly-ev-info">в этом окне конверсий нет</span>
      </div>
      <div v-if="rest.length" class="ly-ev-rest">
        <button v-for="e in rest" :key="e.id" type="button" class="ly-ev-chip" :class="{ 'is-on': e.id === selected }"
                :style="{ '--c': EV[e.kind].c }" @click="pick(e.id)">
          <i></i><b>{{ e.id + 1 }} · {{ e.title }}</b><s>{{ dayLabel(e.day) }}</s>
        </button>
      </div>
    </template>

    <!-- деталь выбранного события -->
    <div v-if="sel" class="ly-ev-detail" :style="{ '--c': EV[sel.kind].c }">
      <div class="ly-ed-h">
        <b>{{ EV[sel.kind].i }}</b>
        <span class="ly-ed-t">№{{ sel.id + 1 }} · {{ sel.title }}<em> · {{ sel.at === 'gap' && sel.gap
          ? 'между сессиями №' + (sel.gap[0] + 1) + ' и №' + (sel.gap[1] + 1) : 'внутри сессии №' + (sel.i + 1) }}</em></span>
        <span class="ly-ed-ty">{{ EV[sel.kind].n }}</span>
        <button type="button" class="tgl ly-ghost ly-ed-x" @click="$emit('select-event', null)">× закрыть</button>
      </div>
      <div class="ly-ed-b">{{ sel.note }}</div>
      <div class="ly-ed-m">
        <div v-for="m in meta" :key="m.k">{{ m.k }}<b>{{ m.v }}</b></div>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import { dayLabel, monthLabel, noChurnTxt } from '@/utils/layers';

// Панель событий под сценой: журнал (для режимов в сцене), «лента» и «воронка», деталь.
// События — готовый список deriveEvents от сцены; здесь только окно ленты и раскладка.
const EV = {
  conv: { c: 'var(--ev-goal)', i: '✓', n: 'конверсия' },
  chg: { c: 'var(--ev-drop)', i: '⇄', n: 'изменение' },
  mile: { c: 'var(--ev-peak)', i: '▲', n: 'веха' },
};
const EV_ORDER = ['conv', 'chg', 'mile'];
const IN_SCENE = ['pins', 'tint', 'card'];
const dateOf = day => new Date(day * 864e5);          // московские сутки → UTC-дата
const CONV_PREFIX = 'Конверсия: ';

export default {
  name: 'LayerEvents',
  props: {
    events: { type: Array, default: () => [] },
    mode: { default: null },
    selected: { default: null },
    range: { default: null },                         // окно ленты времени или null — весь период
    units: { type: Array, default: () => [] },        // хронологический ряд — вход deriveEvents; даёт фразу и даты пары
  },
  emits: ['select-event', 'mode'],
  data() {
    return { EV, EV_ORDER, DAYS: ['день', 'дня', 'дней'], CONV: ['конверсия', 'конверсии', 'конверсий'], W: 800 };
  },
  computed: {
    all() {
      const r = this.range;
      // события «между» при сортировке по блоку сцена уже отфильтровала;
      // событие в разрыве длится от сессии до сессии — берём, если отрезок задевает окно (П 6208)
      if (!r) return this.events;
      return this.events.filter(e => {
        const a = e.at === 'gap' && e.gap ? this.units[e.gap[0]] : null, b = a && this.units[e.gap[1]];
        const d0 = b ? a.day : e.day, d1 = b ? b.day : e.day;
        return d1 >= r.from && d0 <= r.to;
      });
    },
    inScene() { return IN_SCENE.includes(this.mode); },
    // «стыки» живут только в сцене, «нет» — ничего; остальным нужен хотя бы один пункт
    shown() { return this.all.length > 0 && (this.inScene || this.mode === 'score' || this.mode === 'funnel'); },
    // шкала — период сессий столбца, а не самих событий (П 6325)
    minD() { return this.units.length ? this.units[0].day : 0; },
    maxD() { return this.units.length ? this.units[this.units.length - 1].day : 0; },
    span() { return Math.max(1, this.maxD - this.minD); },
    hint() {
      return this.mode === 'tint' ? 'столб сессии красит её карточку, событие между сессиями стоит в разрыве'
        : this.mode === 'card' ? 'клик по событию сессии выдвигает карточку из ряда'
          : 'номера совпадают с метками в сцене';
    },
    // подписи точек расставляем так, чтобы не наезжали: ширину берём из разметки
    lanes() {
      const W = Math.max(320, this.W - 132);
      return EV_ORDER.map(k => {
        let cursor = -1e9;
        const dots = this.all.filter(e => e.kind === k).map(e => {
          const xp = (e.day - this.minD) / this.span;
          // ширина подписи — с номером «N · »; не влезает до правого края — подпись слева от точки
          const x = xp * W, est = (e.title.length + String(e.id + 1).length + 3) * 6.3 + 22;
          const flip = x + 9 + est > W;
          const lab = flip ? x - 9 - est > cursor : x + 9 > cursor;
          if (lab) cursor = flip ? x + 9 : x + 9 + est;
          return { e, x: xp * 100, lab, flip };
        });
        // событие в разрыве длится от одной сессии пары до другой — полосой, если она шире 4 px
        const bars = [];
        this.all.forEach(e => {
          if (e.kind !== k || e.at !== 'gap' || !e.gap) return;
          const a = this.units[e.gap[0]], b = this.units[e.gap[1]];
          if (!a || !b) return;
          const x0 = Math.max(0, (a.day - this.minD) / this.span), x1 = Math.min(1, (b.day - this.minD) / this.span);
          if ((x1 - x0) * W > 4) bars.push({ id: e.id, x: x0 * 100, w: (x1 - x0) * 100 });
        });
        return { k, dots, bars };
      });
    },
    axis() {
      const out = [];
      let mk = null;
      for (let d = this.minD; d <= this.maxD; d++) {
        const dt = dateOf(d), k = dt.getUTCFullYear() * 12 + dt.getUTCMonth();
        if (k !== mk) { out.push({ x: (d - this.minD) / this.span * 100, t: monthLabel(d) }); mk = k; }
      }
      return out;
    },
    // Ступень — первая сессия каждого типа конверсии по хронологии; между ступенями срок и
    // сколько конверсионных сессий набежало. Типы берём из заголовка deriveEvents:
    // имена convTypes фиксированы и запятых не содержат.
    steps() {
      const convs = this.all.filter(e => e.kind === 'conv').sort((a, b) => a.i - b.i);
      const seen = new Set(), out = [];
      convs.forEach((e, idx) => {
        const types = e.title.startsWith(CONV_PREFIX) ? e.title.slice(CONV_PREFIX.length).split(', ') : [e.title];
        const fresh = types.filter(t => !seen.has(t));
        if (!fresh.length) return;
        fresh.forEach(t => seen.add(t));
        const prev = out[out.length - 1];
        out.push({ e, type: fresh.join(', '), acc: idx + 1, tot: convs.length,
          dd: prev ? e.day - prev.e.day : 0, gain: prev ? idx + 1 - prev.acc : 0 });
      });
      return out;
    },
    rest() {
      const ids = new Set(this.steps.map(s => s.e.id));
      return this.all.filter(e => !ids.has(e.id));
    },
    sel() { return this.selected == null ? null : this.all.find(e => e.id === this.selected) || null; },
    meta() {
      const e = this.sel, us = this.units;
      const u = k => (k >= 0 && k < us.length ? us[k] : null);
      const out = [];
      // состав и порядок строк как у П evMeta (6295–6309); у события в разрыве фраза и итог —
      // первой сессии пары. «Итог захода» у нас — где случился отток
      const gap = e.at === 'gap' && e.gap;
      const a = u(gap ? e.gap[0] : e.i), b = gap ? u(e.gap[1]) : null;
      out.push({ k: 'когда', v: gap && a && b ? this.dayLabel(a.day) + ' → ' + this.dayLabel(b.day) : this.dayLabel(e.day) });
      if (gap) out.push({ k: 'между сессиями', v: `№${e.gap[0] + 1} и №${e.gap[1] + 1}` });
      else out.push({ k: 'сессия', v: `№${e.i + 1}` + (us.length ? ` из ${us.length}` : '') });
      if (a) {
        out.push({ k: 'фраза', v: a.phrase });
        out.push({ k: 'итог сессии', v: a.churn >= 0 ? 'отток в блоке ' + (a.churn + 1) : noChurnTxt(a) });
      }
      if (gap && a && b) {
        const d = b.day - a.day;
        out.push({ k: 'длина паузы', v: d > 0 ? d + ' ' + this.plural(d, this.DAYS) : 'в тот же день' });
      }
      return out;
    },
  },
  mounted() {
    // ширина меняется не только с окном браузера: панель появляется при смене режима и окна ленты
    if (typeof ResizeObserver !== 'undefined' && this.$el instanceof Element) {
      this.ro = new ResizeObserver(this.measure);
      this.ro.observe(this.$el);
    }
    this.measure();
  },
  beforeUnmount() { if (this.ro) this.ro.disconnect(); },
  methods: {
    plural(n, f) {
      const a = Math.abs(n) % 100, b = a % 10;
      return a > 10 && a < 20 ? f[2] : b > 1 && b < 5 ? f[1] : b === 1 ? f[0] : f[2];
    },
    dayLabel,
    where(e) { return e.at === 'gap' && e.gap ? `между ${e.gap[0] + 1} и ${e.gap[1] + 1}` : `в сессии ${e.i + 1}`; },
    pick(id) { this.$emit('select-event', id === this.selected ? null : id); },
    measure() { if (this.$el && this.$el.clientWidth) this.W = this.$el.clientWidth; },
  },
};
</script>

<style>
.ins-root .ly-evbar{border:1px solid var(--border); border-radius:12px; background:var(--page);
  padding:10px 14px 12px; margin:14px 0 4px;}
.ins-root .ly-ev-head{display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-bottom:10px;}
.ins-root .ly-ev-info{font-size:12px; color:var(--text-muted); font-variant-numeric:tabular-nums;}
.ins-root .ly-ev-lg{display:inline-flex; align-items:center; gap:4px; font-size:11px; font-weight:600;}
.ins-root .ly-ev-lg b{font-size:10px;}
.ins-root .ly-ev-score{display:flex; flex-direction:column; gap:3px;}
.ins-root .ly-ev-lane{display:grid; grid-template-columns:96px 1fr; align-items:center; gap:8px;
  border-top:1px solid var(--grid); padding:3px 0;}
.ins-root .ly-ev-lname{font-size:11px; font-weight:650;}
.ins-root .ly-ev-track{position:relative; height:30px;}
.ins-root .ly-ev-range{position:absolute; top:12px; height:5px; border-radius:3px; background:var(--c); opacity:.4;}
.ins-root .ly-ev-dot{position:absolute; top:4px; border:0; background:none; padding:0; cursor:pointer; font-family:inherit;
  display:inline-flex; align-items:center; gap:6px; color:var(--c); white-space:nowrap; transform:translateX(-4px);}
.ins-root .ly-ev-dot.ly-ev-flip{flex-direction:row-reverse; transform:translateX(calc(4px - 100%));}
.ins-root .ly-ev-dot > i{width:8px; height:8px; border-radius:50%; background:var(--c); flex:0 0 auto;
  box-shadow:0 0 0 2px var(--page);}
.ins-root .ly-ev-dot > span{font-size:10.5px; font-weight:600; background:var(--surface-1); border:1px solid var(--border);
  border-radius:999px; padding:2px 9px; color:var(--text-secondary);}
.ins-root .ly-ev-dot:hover > span, .ins-root .ly-ev-dot.is-on > span{border-color:var(--c); color:var(--c);}
.ins-root .ly-ev-dot.is-on > span{background:var(--c); color:#fff;}
.ins-root .ly-ev-axis{position:relative; height:14px; margin-left:104px;}
.ins-root .ly-ev-axis span{position:absolute; font-size:10px; color:var(--text-muted); transform:translateX(-50%);}

.ins-root .ly-ev-funnel{display:flex; align-items:stretch; flex-wrap:wrap; gap:0; margin-bottom:6px;}
.ins-root .ly-ev-step{border:1.5px solid var(--ev-goal); background:var(--surface-1); border-radius:12px;
  padding:8px 14px; cursor:pointer; font-family:inherit; text-align:left;
  display:flex; flex-direction:column; gap:1px; min-width:112px;}
.ins-root .ly-ev-step b{font-size:14.5px; font-weight:700; color:var(--ev-goal); line-height:1.2;}
.ins-root .ly-ev-step s{text-decoration:none; font-size:11.5px; color:var(--text-primary); font-weight:600;}
.ins-root .ly-ev-step em{font-style:normal; font-size:10.5px; color:var(--text-muted);}
.ins-root .ly-ev-step.is-on{background:var(--ev-goal); border-color:var(--ev-goal);}
.ins-root .ly-ev-step.is-on b, .ins-root .ly-ev-step.is-on s{color:#fff;}
.ins-root .ly-ev-step.is-on em{color:rgba(255,255,255,.8);}
.ins-root .ly-ev-arrow{display:flex; flex-direction:column; align-items:center; justify-content:center; padding:0 10px; min-width:86px;}
.ins-root .ly-ev-arrow i{display:block; width:100%; height:2px; background:var(--grid); border-radius:1px; position:relative;}
.ins-root .ly-ev-arrow i::after{content:""; position:absolute; right:-1px; top:-3px; border:4px solid transparent;
  border-left-color:var(--grid);}
.ins-root .ly-ev-arrow span{font-size:10px; color:var(--text-muted); text-align:center; line-height:1.3; margin-top:4px;
  font-variant-numeric:tabular-nums;}
.ins-root .ly-ev-rest{display:flex; flex-wrap:wrap; gap:6px; margin-top:8px; padding-top:8px; border-top:1px solid var(--grid);}
.ins-root .ly-ev-chip{display:inline-flex; align-items:center; gap:6px; border:1px solid var(--border); border-radius:999px;
  background:var(--surface-1); padding:4px 11px; font-size:11.5px; cursor:pointer; font-family:inherit;
  color:var(--text-secondary);}
.ins-root .ly-ev-chip i{width:8px; height:8px; border-radius:50%; background:var(--c); flex:0 0 auto;}
.ins-root .ly-ev-chip b{font-weight:650; color:var(--text-primary);}
.ins-root .ly-ev-chip s{text-decoration:none; color:var(--text-muted); font-variant-numeric:tabular-nums;}
.ins-root .ly-ev-chip.is-on{border-color:var(--c); box-shadow:0 0 0 1px var(--c);}

.ins-root .ly-ev-log{display:grid; grid-template-columns:repeat(auto-fill, minmax(365px, 1fr)); gap:3px 16px;}
.ins-root .ly-ev-row{display:grid; grid-template-columns:20px 9px minmax(0,1fr) auto auto; align-items:center; gap:8px;
  border:0; background:none; font-family:inherit; text-align:left; cursor:pointer; padding:5px 6px;
  border-radius:8px; color:var(--text-secondary);}
.ins-root .ly-ev-row:hover{background:var(--surface-1);}
.ins-root .ly-ev-row u{text-decoration:none; font-size:10px; font-weight:700; color:var(--text-muted);
  font-variant-numeric:tabular-nums; text-align:right;}
.ins-root .ly-ev-row i{width:9px; height:9px; border-radius:50%; background:var(--c);}
.ins-root .ly-ev-row b{font-size:12.5px; font-weight:600; color:var(--text-primary); overflow:hidden;
  text-overflow:ellipsis; white-space:nowrap;}
.ins-root .ly-ev-row em{font-style:normal; font-size:10.5px; color:var(--text-muted); white-space:nowrap;}
.ins-root .ly-ev-row s{text-decoration:none; font-size:11px; color:var(--text-muted); font-variant-numeric:tabular-nums;}
.ins-root .ly-ev-row.is-on{background:var(--surface-1); box-shadow:inset 0 0 0 1px var(--c);}
.ins-root .ly-ev-row.is-on b{color:var(--c);}

.ins-root .ly-ev-detail{margin-top:10px; border-top:2px solid var(--c); background:var(--surface-1);
  border-radius:0 0 10px 10px; padding:10px 12px 12px;}
.ins-root .ly-ed-h{display:flex; align-items:center; gap:8px; flex-wrap:wrap;}
.ins-root .ly-ed-h > b{color:var(--c); font-size:13px;}
.ins-root .ly-ed-t{font-size:14px; font-weight:700; color:var(--text-primary);}
.ins-root .ly-ed-t em{font-style:normal; color:var(--text-muted); font-weight:500;}
.ins-root .ly-ed-ty{font-size:10.5px; font-weight:700; letter-spacing:.04em; text-transform:uppercase; color:var(--c);
  border:1px solid var(--c); border-radius:999px; padding:1px 8px;}
/* «× закрыть» — chip ghosty, как кнопки LayerBar */
.ins-root .ly-ed-x{margin-left:auto; padding:5px 10px; font-size:12.5px; color:var(--text-muted);}
.ins-root .ly-ed-b{font-size:12.5px; color:var(--text-secondary); line-height:1.5; margin:7px 0 8px; max-width:760px;}
.ins-root .ly-ed-m{display:flex; flex-wrap:wrap; gap:6px 18px;}
.ins-root .ly-ed-m div{font-size:11.5px; color:var(--text-muted);}
.ins-root .ly-ed-m div b{display:block; font-size:13px; color:var(--text-primary); font-weight:650;
  font-variant-numeric:tabular-nums;}
</style>
